/**
 *
 * EasySplitPayments
 *
 */

import React, { memo, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import moment from 'moment';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_OPTIONS,
  DEFAULT,
  DATE_AND_TIME_FORMAT,
  PLACEHOLDER_TEXT,
  PAYMENTS,
} from 'utils/constants';
import {
  addCurrencySymbol,
  roundOffNumber,
  exportToCSV as exportToCSVFn,
  copyToClipboard,
  removeCharacter,
} from 'utils/common';
import { Button, Conditional, Text, Space } from '@cashfree-intl/coherent';
import PaginatedTable from 'components/PaginatedTable';
import { formatColumns } from 'components/PaginatedTable/helpers';

import CustomIcon from '../../components/CustomIcons';
import makeSelectEasySplitPayments from './selectors';
import reducer from './reducer';
import saga from './saga';
import { HEADINGS } from './constants';
import { resetTwoFactorAuthentication as resetTwoFactorAuthenticationValue } from '../Toast/actions';
import { twoFactorAuthenticationSelector } from '../Toast/selectors';
import {
  enableLoaderAction,
  resetFiltersAction,
  resetPageAction,
  searchPaymentsAction,
  updateFiltersAction,
  updateTableDataAction,
  exportToCSVAction,
} from './actions';
import { DashboardFilters } from '../DashboardFilters';

import { basename } from '../../store';
import { EASY_SPLIT_KNOW_MORE_LINK } from '../EasySplit/constants';
import { StyledSubHeader } from './styled';

const renderWithCopyToClipboard = value => (
  <div className="flex copy-to-clipboard-wrapper">
    <span className="table-content">{value || PLACEHOLDER_TEXT}</span>
    <Conditional if={value && value !== 'N/A'}>
      <CustomIcon
        componentName="COPY_TO_CLIPBOARD"
        handleClick={copyToClipboard(value)}
      />
    </Conditional>
  </div>
);

export function EasySplitPayments({
  searchPayments,
  menuSelected,
  resetFilters,
  updateFilters,
  resetPage,
  updateTableData,
  enableLoader,
  exportToCSV,
  twoFactorAuthentication,
  resetTwoFactorAuthentication,
  easySplitPayments,
}) {
  useInjectReducer({ key: 'easySplitPayments', reducer });
  useInjectSaga({ key: 'easySplitPayments', saga });

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { pathname, hash, state = {} } = location;

  const {
    isLoading,
    filters,
    filtersForDisplay,
    hasPrev,
    hasNext,
    currentPageRowData,
    data,
  } = easySplitPayments;

  const rowClicked = useRef(false);
  const activeNavigation = DEFAULT;

  const initActiveRowId = () => {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!state) {
      const { rowId } = state;
      if (rowId) rowClicked.current = false;
      return rowId;
    }
    return null;
  };
  const [dataForExport, setDataForExport] = useState({});
  const [activeRowId, setActiveRowId] = useState(initActiveRowId());

  useEffect(() => {
    if (activeRowId) {
      setTimeout(() => {
        setActiveRowId(null);
      }, 8000);
    }
  }, [activeRowId]);

  useEffect(() => {
    getTableData(filters);
  }, [filters, searchParams.get('env')]);

  const getTableData = (f = filters) => {
    enableLoader();

    const { appliedFilters, pageNo, pageSize } = f;

    const formattedFilterObj = {
      ...appliedFilters,
      pageNo,
      pageSize,
    };

    if (
      appliedFilters.search &&
      appliedFilters.search.type &&
      appliedFilters.search.value
    ) {
      const {
        search: { type, value },
      } = appliedFilters;
      formattedFilterObj[type] = value;
      delete formattedFilterObj.search;
    }

    setDataForExport({
      options: {
        ...formattedFilterObj,
        isDownloaderRequest: true,
      },
      cb: exportToCSVFn,
    });

    searchPayments({
      options: formattedFilterObj,
    });
  };

  useEffect(() => {
    if (!isLoading) {
      const totalCount = data.length;
      const pageData = JSON.parse(JSON.stringify(data));
      let formattedData = [];

      formattedData = pageData.map(rowData => ({
        ...rowData,
        id: rowData.orderId,
        primaryKey: rowData.transactionId,
        displayOrderId: rowData.orderId,
        createdAt:
          moment(rowData.txTime).format(DATE_AND_TIME_FORMAT) ||
          PLACEHOLDER_TEXT,
        orderId: () => renderWithCopyToClipboard(rowData.orderId),
        customerName: rowData.customerName || PLACEHOLDER_TEXT,
        orderAmount: () => (
          <span>{addCurrencySymbol(roundOffNumber(rowData.orderAmount))}</span>
        ),
        transactionId: () => renderWithCopyToClipboard(rowData.transactionId),
        txnAmount: () => (
          <span>{addCurrencySymbol(roundOffNumber(rowData.txnAmount))}</span>
        ),
        customerPhone: () => renderWithCopyToClipboard(rowData.customerPhone),
        paymentMode: () => (
          <div className="flex copy-to-clipboard-wrapper">
            <span className="table-content mw-100">
              {removeCharacter(rowData.txnPaymentMode, '_', ' ')}
            </span>
          </div>
        ),
      }));

      const hasPrevEnabled = filters.pageNo !== 1;
      const hasNextEnabled = totalCount >= filters.pageSize;

      updateTableData({
        totalCount,
        currentPageRowData: formattedData,
        hasPrevEnabled,
        hasNextEnabled,
      });
    }
  }, [isLoading, data]);

  useEffect(() => {
    if (twoFactorAuthentication) {
      getTableData();
      resetTwoFactorAuthentication(false);
    }
    return () => {
      if (!rowClicked.current) resetFilters();
    };
  }, [twoFactorAuthentication]);

  const onPageChange = type => {
    const options = { ...filters, filtersForDisplay };
    switch (type) {
      case 'NEXT':
        options.pageNo += 1;
        break;
      case 'PREV':
        options.pageNo -= 1;
        break;
      default:
        break;
    }
    updateFilters(options);
  };

  const onLimitChange = (e, limitChangeData) => {
    const options = {
      ...filters,
      pageNo: DEFAULT_CURRENT_PAGE,
      pageSize: parseInt(limitChangeData.value, 10),
      filtersForDisplay,
    };
    updateFilters(options);
    // Analytics.track(EVENTS.EASY_SPLIT.PAGE_SIZE, {
    //   pageSize: limitChangeData.value,
    // });
  };

  const handleExportButtonClick = () => {
    enableLoader();
    exportToCSV(dataForExport);
  };

  const handleRowClick = rowData => {
    const { primaryKey, displayOrderId, id } = rowData;
    const { env = '' } = Object.fromEntries(searchParams);

    rowClicked.current = true;

    // taking user to the timeline component
    navigate(
      `${pathname}/view?activeNavigation=${PAYMENTS}&txId=${primaryKey}&orderId=${displayOrderId}${
        env ? `&env=${env}` : ''
      }${hash}`,
      {
        rowId: id,
      },
    );
  };

  const handleNotFoundCTAClick = () => {
    navigate(
      `${basename}pg/payment-links?createPaymentLink=true${
        searchParams.get('env') ? `&env=${searchParams.get('env')}` : ''
      }`,
    );
  };

  const noRecordsObj = {
    icon: <CustomIcon componentName="BOX" />,
    text: <Text variant="h16">No payments found</Text>,
    description: (
      <Text color="bodyLight" variant="b14">
        Start transacting with your customers to see the payment details here.
      </Text>
    ),
    action: (
      <Space gap={2} direction="column" alignItems="center">
        <Button secondary onClick={handleNotFoundCTAClick}>
          Create Payment Link
        </Button>

        <a
          href="https://www.cashfree.com/docs/payments/overview"
          target="_blank"
        >
          Read Integration docs
        </a>
      </Space>
    ),
  };

  const FORMATTED_COLUMN_ID = formatColumns(HEADINGS);

  return (
    <div className="easy-split-payments">
      <StyledSubHeader>
        <Text className="text-light mt-0">
          All vendors are shown here.{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            href={EASY_SPLIT_KNOW_MORE_LINK}
            target="_blank"
            data-event-name="Link_KnowMore"
          >
            Know more
          </a>
        </Text>
      </StyledSubHeader>
      <div className="filters-wrapper">
        <DashboardFilters
          menuSelected={menuSelected}
          filters={filters}
          filtersForDisplay={filtersForDisplay}
          activeNavigation={activeNavigation}
          getTableDataFn={getTableData}
          resetFilterFn={resetFilters}
          updateFilterFn={updateFilters}
          resetPageFn={resetPage}
        />
        <div className="tab-cta-wrapper">
          <div>
            <Button
              primary
              onClick={handleExportButtonClick}
              className="cta-item"
              disabled={currentPageRowData.length === 0 && !hasPrev}
              data-event-name="Primary_Button_Export"
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      <PaginatedTable
        data={currentPageRowData}
        emptyTableComponent={noRecordsObj}
        tableHeading={FORMATTED_COLUMN_ID}
        limit={filters.pageSize}
        limitOptions={DEFAULT_LIMIT_OPTIONS}
        hasPrev={hasPrev}
        hasNext={hasNext}
        currentPage={filters.pageNo}
        currentPageCount={currentPageRowData.length}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        onRowClick={handleRowClick}
        fetching={isLoading}
      />
    </div>
  );
}

EasySplitPayments.propTypes = {
  searchPayments: PropTypes.func.isRequired,
  menuSelected: PropTypes.any.isRequired,
  resetFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetPage: PropTypes.func.isRequired,
  easySplitPayments: PropTypes.object.isRequired,
  updateTableData: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  exportToCSV: PropTypes.func.isRequired,
  twoFactorAuthentication: PropTypes.bool.isRequired,
  resetTwoFactorAuthentication: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  easySplitPayments: makeSelectEasySplitPayments(),
  twoFactorAuthentication: twoFactorAuthenticationSelector(),
});

export function mapDispatchToProps(dispatch) {
  return {
    searchPayments: data => dispatch(searchPaymentsAction(data)),
    resetFilters: () => dispatch(resetFiltersAction()),
    updateFilters: data => dispatch(updateFiltersAction(data)),
    resetPage: () => dispatch(resetPageAction()),
    updateTableData: data => dispatch(updateTableDataAction(data)),
    enableLoader: () => dispatch(enableLoaderAction()),
    exportToCSV: data => dispatch(exportToCSVAction(data)),
    resetTwoFactorAuthentication: data =>
      dispatch(resetTwoFactorAuthenticationValue(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(EasySplitPayments);
